import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import useAppToast from 'src/hooks/useAppToast';
import {
  deleteMapValuesMapping,
  editOptionsAttributeMapping,
  getOptionAttributeMappingById,
  getOptionsAttributeMappingForSelectedId,
  getOptionsForAttributeMapping,
  saveOptionsAttributeMapping,
} from 'src/redux/actions/AttributeAndOptionValueMappingActions';
import { useAppDispatch } from 'src/redux/storeHooks';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faChevronDown,
  faTrashAlt,
  faMinus,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import CommonAlertModal from 'src/utils/modals/CommonAlertModal';
import AttributeMappingSelect from './AttributeMappingSelect';

type Option = {
  id: number;
  attribute_field: {
    display_name: string;
  };
};

type Props = {
  dataForEdit: any;
  item: any;
  showOptionsAndAttributeModal: boolean;
  setShowOptionsAndAttributeModal: (value: boolean) => void;
  getOptionAttributeMaping: () => void;
};

const AttributeAndOptionValueMappingModal = ({
  dataForEdit,
  item,
  showOptionsAndAttributeModal,
  setShowOptionsAndAttributeModal,
  getOptionAttributeMaping,
}: Props) => {
  const dispatch = useAppDispatch();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { successToast, warningToast, errorToast } = useAppToast();

  const [formState, setFormState] = useState({
    searchTerm: '',
    selectedValue: null as number | null,
    selectedOptionText: '',
    mappingName: '',
  });
  const [mappings, setMappings] = useState<Record<string, number>>({});
  const [showOptions, setShowOptions] = useState(false);
  const [fetchedOptions, setFetchedOptions] = useState<Option[]>([]);
  const [loadingStates, setLoadingStates] = useState({
    optionsList: false,
    optionValues: false,
    searchLoading: false, // Add separate loading state for search
    isSaving: false,
  });
  const [optionData, setOptionData] = useState<any>();
  const [dropDownData, setDropDownData] = useState<any>();
  const [attributeFieldId, setAttributeFieldId] = useState<number>();
  const [mappedFieldValue, setMappedFieldValue] = useState<number>();
  const [deleteModel, setDeleteModel] = useState<boolean>(false);
  const [selectMapId, setSelectedMapId] = useState<number | null>(null);
  const [skipFieldData, setSkipFieldData] = useState<number>(0);
  const [lastOptionValueSearch, setLastOptionValueSearch] = useState<string>('');
  const [optionValueCount, setOptionValueCount] = useState<number>(0);
  const [optionValuePageSize, setOptionValuePageSize] = useState<number>(5);
  const [destinationOptionPageSize, setDestinationOptionPageSize] = useState<number>(10);
  const [hasMoreDestinationOptions, setHasMoreDestinationOptions] = useState<boolean>(true);
  const [selectedOptionLabels, setSelectedOptionLabels] = useState<Record<number, string>>({});
  const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);
  const destinationAttributeSkipRef = useRef(0);
  const destinationFetchGenerationRef = useRef(0);
  const destinationLoadMoreInFlightRef = useRef<number | null>(null);

  const invalidateDestinationFetchContext = () => {
    destinationFetchGenerationRef.current += 1;
    destinationAttributeSkipRef.current = 0;
    destinationLoadMoreInFlightRef.current = null;
    setLoadingStates((prev) => ({
      ...prev,
      searchLoading: false,
      optionValues: false,
    }));
  };

  const [payloadForSave, setPayloadForSave] = useState<{
    mapped_option_value: {
      id: number;
      attribute_data_id: number;
      platformfielddata_id: number;
    }[];
  }>({
    mapped_option_value: [],
  });

  const getOptionLabel = (option: any) => option?.api_name || option?.api_code || "";

  const resetForm = () => {
    setFormState({ searchTerm: '', selectedValue: null, selectedOptionText: '', mappingName: '' });
    setMappings({});
    setPayloadForSave({ mapped_option_value: [] });
    setOptionData(null);
    setDropDownData(null);
    setAttributeFieldId(undefined);
    setMappedFieldValue(undefined);
    setShowOptions(false);
    setSkipFieldData(0);
    setLastOptionValueSearch('');
    setOptionValueCount(0);
    setOptionValuePageSize(5);
    setDestinationOptionPageSize(10);
    setHasMoreDestinationOptions(true);
    invalidateDestinationFetchContext();
    setSelectedOptionLabels({});
    setOpenDropdownId(null);
  };

  const handleClose = () => {
    setShowOptionsAndAttributeModal(false);
    if (dataForEdit === null) resetForm();
  };

  const handleOptionClick = (id: number, label: string) => {
    setFormState((prev) => ({
      ...prev,
      selectedValue: id,
      selectedOptionText: label,
      searchTerm: label,
    }));
    setShowOptions(false);
    setMappings({});
    setSkipFieldData(0);
    setLastOptionValueSearch('');
    setOptionValueCount(0);
    setOptionValuePageSize(5);
    setDestinationOptionPageSize(10);
    setHasMoreDestinationOptions(true);
    invalidateDestinationFetchContext();
    setDropDownData(null);
    setSelectedOptionLabels({});
  };

  const fetchOptionsList = useCallback(async () => {
    setLoadingStates((prev) => ({ ...prev, optionsList: true }));
    try {
      setFetchedOptions([]);
      let allOptions: Option[] = [];
      let skip = 0;
      let totalCount: number | null = null;
      let hasMore = true;

      while (hasMore) {
        const response = await dispatch(
          getOptionsForAttributeMapping({ platformId: item.id, skip })
        );
        const payload = response?.payload;
        const data: Option[] = Array.isArray(payload?.data) ? payload.data : [];

        totalCount = payload?.count ?? totalCount ?? data.length;

        const existingIds = new Set(allOptions.map((opt) => opt.id));
        const uniqueOptions = data.filter((opt) => !existingIds.has(opt.id));

        allOptions = [...allOptions, ...uniqueOptions];
        skip = allOptions.length;

        const reachedCount = totalCount !== null && allOptions.length >= totalCount;
        hasMore = !reachedCount && uniqueOptions.length > 0;
      }

      setFetchedOptions(allOptions);
    } catch (error) {
      console.error('Error fetching options:', error);
      setFetchedOptions([]);
    } finally {
      setLoadingStates((prev) => ({ ...prev, optionsList: false }));
    }
  }, [dispatch, item?.id]);

  // Updated fetchOptionValues to handle search text
  const fetchOptionValues = async (searchText?: string, preserveOptionData = false) => {
    if (!formState.selectedValue) return;

    const activeSearch = searchText || '';
    setLastOptionValueSearch(activeSearch);
    const requestGeneration = destinationFetchGenerationRef.current + 1;
    destinationFetchGenerationRef.current = requestGeneration;
    destinationAttributeSkipRef.current = 0;
    destinationLoadMoreInFlightRef.current = null;

    // Preserve mapping section while destination dropdown loads/searches.
    const loadingKey = (activeSearch || preserveOptionData) ? 'searchLoading' : 'optionValues';
    if (loadingKey === 'optionValues') {
      setDropDownData(null);
    }
    setLoadingStates((prev) => ({
      ...prev,
      searchLoading: false,
      optionValues: false,
      [loadingKey]: true,
    }));
    
    try {
      const sourceFieldOffset = skipFieldData * optionValuePageSize;
      const response = await dispatch(
        getOptionsAttributeMappingForSelectedId({
          platformId: item.id,
          attribute_template_id: formState.selectedValue,
          skip_field_data: sourceFieldOffset,
          skip_attribute_data: 0,
          search: activeSearch, // Add search text parameter
        })
      );
      if (requestGeneration !== destinationFetchGenerationRef.current) return;

      const payload = response?.payload;
      const data = Array.isArray(payload?.data) ? payload.data : [];
      const totalCount =
        typeof payload?.count === 'number'
          ? payload.count
          : data?.[0]?.mapped_field?.platformfielddata_count || 0;

      setOptionValueCount(totalCount);

      if (data.length > 0) {
        const pageSize = data[0]?.mapped_field?.platformfielddata?.length || 0;
        if (pageSize > 0) {
          setOptionValuePageSize(pageSize);
        }
        const latestAttributeData = Array.isArray(data[0]?.attribute_field?.attribute_data)
          ? data[0].attribute_field.attribute_data
          : [];
        const optionBatchSize = latestAttributeData.length > 0
          ? latestAttributeData.length
          : destinationOptionPageSize;
        if (optionBatchSize > 0) {
          setDestinationOptionPageSize(optionBatchSize);
        }
        setHasMoreDestinationOptions(latestAttributeData.length > 0);
        if (latestAttributeData.length > 0) {
          setSelectedOptionLabels((prev) => {
            const next = {...prev};
            latestAttributeData.forEach((opt) => {
              const optionLabel = getOptionLabel(opt);
              if (opt?.id && optionLabel) {
                next[opt.id] = optionLabel;
              }
            });
            return next;
          });
        }
        destinationAttributeSkipRef.current = latestAttributeData.length;
        setDropDownData(latestAttributeData);

        if (!preserveOptionData || !optionData?.length) {
          setOptionData(data);
          setAttributeFieldId(data[0]?.attribute_field?.id);
          setMappedFieldValue(data[0]?.mapped_field?.id);
        }
      } else {
        destinationAttributeSkipRef.current = 0;
        setDropDownData([]);
        setHasMoreDestinationOptions(false);
        if (!activeSearch) {
          setOptionData([]);
          setAttributeFieldId(undefined);
          setMappedFieldValue(undefined);
        }
      }
    } catch (err) {
      console.error('Error fetching mapping values:', err);
      if (requestGeneration === destinationFetchGenerationRef.current) {
        setDropDownData([]);
        destinationAttributeSkipRef.current = 0;
        setHasMoreDestinationOptions(false);
      }
    } finally {
      if (requestGeneration === destinationFetchGenerationRef.current) {
        setLoadingStates((prev) => ({ ...prev, [loadingKey]: false }));
      }
    }
  };

  const fetchMoreOptionValues = async (searchText: string) => {
    const requestGeneration = destinationFetchGenerationRef.current;
    if (
      !formState.selectedValue ||
      !hasMoreDestinationOptions ||
      loadingStates.searchLoading ||
      destinationLoadMoreInFlightRef.current === requestGeneration
    ) {
      return;
    }
    const activeSearch = searchText || '';
    const sourceFieldOffset = skipFieldData * optionValuePageSize;
    const nextOffset = destinationAttributeSkipRef.current;

    destinationLoadMoreInFlightRef.current = requestGeneration;
    setLoadingStates((prev) => ({ ...prev, searchLoading: true }));
    try {
      const response = await dispatch(
        getOptionsAttributeMappingForSelectedId({
          platformId: item.id,
          attribute_template_id: formState.selectedValue,
          skip_field_data: sourceFieldOffset,
          skip_attribute_data: nextOffset,
          search: activeSearch,
        })
      );

      if (requestGeneration !== destinationFetchGenerationRef.current) return;

      const payload = response?.payload;
      const data = Array.isArray(payload?.data) ? payload.data : [];
      const nextAttributeData = Array.isArray(data?.[0]?.attribute_field?.attribute_data)
        ? data[0].attribute_field.attribute_data
        : [];

      destinationAttributeSkipRef.current = nextOffset + nextAttributeData.length;

      if (nextAttributeData.length === 0) {
        setHasMoreDestinationOptions(false);
        return;
      }

      setSelectedOptionLabels((prev) => {
        const next = { ...prev };
        nextAttributeData.forEach((opt: any) => {
          const optionLabel = getOptionLabel(opt);
          if (opt?.id && optionLabel) {
            next[opt.id] = optionLabel;
          }
        });
        return next;
      });

      setDropDownData((prev: any) => {
        const previous = Array.isArray(prev) ? prev : [];
        const mergedMap = new Map<number, any>();
        previous.forEach((option: any) => {
          if (option?.id) mergedMap.set(option.id, option);
        });
        const beforeCount = mergedMap.size;
        nextAttributeData.forEach((option: any) => {
          if (option?.id) mergedMap.set(option.id, option);
        });
        const afterCount = mergedMap.size;
        if (afterCount === beforeCount) {
          setHasMoreDestinationOptions(false);
        }
        return Array.from(mergedMap.values());
      });

      if (nextAttributeData.length < destinationOptionPageSize) {
        setHasMoreDestinationOptions(false);
      }
    } catch (error) {
      console.error('Error fetching more destination options:', error);
    } finally {
      if (destinationLoadMoreInFlightRef.current === requestGeneration) {
        destinationLoadMoreInFlightRef.current = null;
      }
      if (requestGeneration === destinationFetchGenerationRef.current) {
        setLoadingStates((prev) => ({ ...prev, searchLoading: false }));
      }
    }
  };

  const getDataForEdit = async () => {
    setLoadingStates((prev) => ({ ...prev, optionValues: true }));
    try {
      const response = await dispatch(getOptionAttributeMappingById({
        platformId: item?.id,
        id: dataForEdit?.id,
      }));

      const responseData = response?.payload?.data;
      const editData = Array.isArray(responseData) ? responseData[0] : responseData;

      if (editData) {
        const mapValueData = editData?.mapped_option_value ?? [];

        const mapped = mapValueData.map((item) => ({
          id: item.id,
          attribute_data_id: item.platform_category_attribute_data.id,
          platformfielddata_id: item.platform_field_data.id,
        }));
        setPayloadForSave({ mapped_option_value: mapped });
        const labelsFromEdit = mapValueData.reduce((acc, item) => {
          const optionId = item?.platform_category_attribute_data?.id;
          const optionLabel =
            item?.platform_category_attribute_data?.api_name ||
            item?.platform_category_attribute_data?.api_code;
          if (optionId && optionLabel) {
            acc[optionId] = optionLabel;
          }
          return acc;
        }, {} as Record<number, string>);
        if (Object.keys(labelsFromEdit).length > 0) {
          setSelectedOptionLabels((prev) => ({...prev, ...labelsFromEdit}));
        }

        const attributeTemplateId =
          editData.platform_category_attribute_template ||
          dataForEdit?.platform_category_attribute_template;
        const matchedOption = fetchedOptions.find((opt) => opt.id === attributeTemplateId);

        const displayName =
          matchedOption?.attribute_field?.display_name ||
          editData?.attribute_field?.display_name ||
          editData?.name ||
          dataForEdit?.name ||
          '';
        setFormState({
          selectedValue: attributeTemplateId,
          selectedOptionText: displayName,
          searchTerm: displayName,
          mappingName: editData.name || dataForEdit?.name || '',
        });

        const newMappings: Record<string, number> = {};
        for (let item of mapValueData) {
          const pfData = item.platform_field_data;
          const catData = item.platform_category_attribute_data;

          newMappings[pfData.api_name] = catData.id;
        }
        setMappings(newMappings);
      }
    } catch (err) {
      console.log("Error loading edit data", err);
    } finally {
      setLoadingStates((prev) => ({ ...prev, optionValues: false }));
    }
  };

  useEffect(() => {
    if (dataForEdit?.id && showOptionsAndAttributeModal) {
      const attributeTemplateId = dataForEdit?.platform_category_attribute_template;
      if (attributeTemplateId) {
        setFormState((prev) => ({
          ...prev,
          selectedValue: attributeTemplateId,
          selectedOptionText: dataForEdit?.name || '',
          searchTerm: dataForEdit?.name || '',
          mappingName: dataForEdit?.name || '',
        }));
      }
      getDataForEdit();
    }
  }, [dataForEdit?.id, showOptionsAndAttributeModal]);

  useEffect(() => {
    if (showOptionsAndAttributeModal) {
      fetchOptionsList();
    }
  }, [showOptionsAndAttributeModal, fetchOptionsList]);

  useEffect(() => {
    if (!showOptionsAndAttributeModal || dataForEdit == null) {
      resetForm();
    }
  }, [showOptionsAndAttributeModal, dataForEdit?.id])

  useEffect(() => {
    if (formState.selectedValue !== null) {
      fetchOptionValues();
    }
  }, [formState.selectedValue]);

  useEffect(() => {
    if (formState.selectedValue !== null) {
      fetchOptionValues();
    }
  }, [skipFieldData]);

  const onSave = async (data: any) => {
    const {
      name,
      platform_id,
      mapped_field_id,
      attribute_template_id,
      attribute_field_id,
      mapped_option_value,
    } = data;

    if (!name || name.trim() === '') {
      warningToast("Name is required");
      return;
    }

    if (!platform_id || !mapped_field_id || !attribute_template_id || !attribute_field_id) {
      warningToast("All fields are required before saving.");
      return;
    }

    if (!mapped_option_value || !Array.isArray(mapped_option_value) || mapped_option_value.length === 0) {
      warningToast("Mapped Option Value is required and cannot be empty");
      return;
    }

    setLoadingStates((prev) => ({ ...prev, isSaving: true }));
    try {
      let response;
      if (dataForEdit !== null) {
        const { id: editId } = dataForEdit;
        response = await dispatch(editOptionsAttributeMapping({
          editId,
          name,
          platform_id,
          mapped_field_id,
          attribute_template_id,
          attribute_field_id,
          mapped_option_value,
        }));
      } else {
        response = await dispatch(saveOptionsAttributeMapping({
          name,
          platform_id,
          mapped_field_id,
          attribute_template_id,
          attribute_field_id,
          mapped_option_value,
        }));
      }

      const res = response?.payload;
      if (res?.status == "200") {
        successToast("Mapping saved successfully");
      } else {
        errorToast(res?.data || "Failed to save mapping");
      }
    } catch (err) {
      console.error("Error saving mapping:", err);
      errorToast("An unexpected error occurred while saving");
    } finally {
      setLoadingStates((prev) => ({ ...prev, isSaving: false }));
      getOptionAttributeMaping();
      handleClose();
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowOptions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDeleteMapping = () => {
    if (!selectMapId || typeof selectMapId !== "number") {
      errorToast("Invalid mapping ID. Cannot proceed with deletion.");
      return;
    }

    dispatch(
      deleteMapValuesMapping({
        platform_attribute_option_data_mapping_id: selectMapId,
      })
    )
      .then((response) => {
        if (response.payload?.success) {
          successToast(response.payload.success);
          getDataForEdit()
          setDeleteModel(false);
        } else {
          errorToast("Failed to delete mapping.");
        }
      })
      .catch((error) => {
        console.error("Error deleting mapping:", error);
        errorToast("An unexpected error occurred while deleting mapping.");
      })
      .finally(() => {
        setSelectedMapId(null);
        setDeleteModel(false);
          fetchOptionValues();
      });
  };

  const sourceOptionValuesLoaded = Array.isArray(optionData?.[0]?.mapped_field?.platformfielddata);
  const destinationOptionValuesLoaded = Array.isArray(dropDownData);
  const shouldShowMappingLoader =
    formState.selectedValue !== null &&
    (loadingStates.optionValues || !sourceOptionValuesLoaded || !destinationOptionValuesLoaded);

  const isFormLoading =
    loadingStates.optionValues ||
    loadingStates.optionsList ||
    (formState.selectedValue !== null && (!sourceOptionValuesLoaded || !destinationOptionValuesLoaded));
  const isSaving = loadingStates.isSaving;

  const optionValuePages = Math.max(
    1,
    Math.ceil((optionValueCount || 0) / optionValuePageSize)
  );

  useEffect(() => {
    if (skipFieldData >= optionValuePages) {
      setSkipFieldData(Math.max(0, optionValuePages - 1));
    }
  }, [optionValuePages, skipFieldData]);
  
  
  return (
    <>
      <Modal
        show={showOptionsAndAttributeModal}
        onHide={handleClose}
        centered
        size="lg"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title className="w-100 text-center">
            Attribute & Option Value Mapping
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* {loadingStates?.optionValues === false &&  */}
          <p className="text-muted text-center mb-4">
            Map specific attribute and option values from your Source System to corresponding values on the Marketplace.
          </p>

          <Form.Group className="mb-4">
            <Form.Label className="fw-semibold required">
              Mapping Name
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter mapping name"
              value={formState.mappingName}
              required
              onChange={(e) =>
                setFormState((prev) => ({ ...prev, mappingName: e.target.value }))
              }
              className="shadow-sm"
            />
          </Form.Group>

          {dataForEdit === null && (
          <Form.Group className="mb-4">
            <Form.Label className="fw-semibold">
              Select Attribute/Option to Map<span className="text-danger">*</span>
            </Form.Label>
            <div className="position-relative" ref={dropdownRef}>
              <Form.Control
                type="text"
                value={formState.searchTerm}
                placeholder={"-- Select an item --"}
                onChange={(e) => {
                  setFormState((prev) => ({
                    ...prev,
                    searchTerm: e.target.value,
                  }));
                  setShowOptions(true);
                }}
                onClick={() => setShowOptions(true)}
                autoComplete="off"
                className="shadow-sm"
              />
              <FontAwesomeIcon
                icon={faChevronDown}
                className="position-absolute end-0 top-50 translate-middle-y me-3 text-muted"
                style={{ pointerEvents: 'none' }}
              />
              {!loadingStates.optionsList && showOptions && (
                <div
                  className="border position-absolute bg-white w-100 rounded shadow-sm"
                  style={{ zIndex: 10, maxHeight: '150px', overflowY: 'auto' }}
                >
                  {fetchedOptions.length ? (
                    fetchedOptions
                      .filter((opt) =>
                        opt.attribute_field.display_name
                          .toLowerCase()
                          .includes(formState.searchTerm.toLowerCase())
                      )
                      .map((opt) => (
                        <div
                          key={opt.id}
                          className="p-2 option-item text-dark hover-bg"
                          style={{ cursor: 'pointer' }}
                          onClick={() =>
                            handleOptionClick(opt.id, opt.attribute_field.display_name)
                          }
                        >
                          {opt.attribute_field.display_name}
                        </div>
                      ))
                  ) : (
                    <div className="p-2 text-muted">No results found</div>
                  )}
                </div>
              )}
            </div>
          </Form.Group>
          )}
          {/* </>} */}

          {/* {mappings.length === 0 ? (
            <div className="d-flex align-items-center justify-content-center my-5">
              <Spinner animation="border" role="status" className="me-2" />
              <span className="text-muted">Loading values...</span>
            </div>
          ) : ( */}
            {/* formState.selectedValue && ( */}
            {shouldShowMappingLoader && (
              <div className="d-flex align-items-center justify-content-center my-5">
                <Spinner animation="border" role="status" className="me-2" />
                <span className="text-muted">Loading source and destination options...</span>
              </div>
            )}

            {!shouldShowMappingLoader && optionData?.[0]?.attribute_field?.display_name !== undefined && 
              <div className="border rounded p-4 mt-4 bg-light shadow-sm">
                <h5 className="mb-3">
                  Map Values for "<strong>{optionData?.[0]?.attribute_field?.display_name}</strong>"
                </h5>
                <p className="text-muted">
                  Match values from your Source System to values on the Marketplace.
                </p>
                <div className="d-flex justify-content-between pb-2 mb-2 text-muted fw-bold border-bottom">
                  <div className="">Source Option Value</div>
                  <div style={{ width: 260 }}>Destination Option Value</div>
                </div>

                {optionData?.[0]?.mapped_field?.platformfielddata?.map((val: any) => (
                  <div
                    key={val.id}
                    className="d-flex align-items-center justify-content-between p-3 mb-1 bg-white rounded shadow-sm"
                  >
                    <div className="flex-grow-1 d-flex align-items-center">
                      <span className="fw-bold">{val.api_name}</span>
                    </div>

                    <div>
                      <AttributeMappingSelect
                        val={val}
                        mappings={mappings}
                        setMappings={setMappings}
                        setPayloadForSave={setPayloadForSave}
                        dropDownData={dropDownData}
                        fetchOptionsForSearch={(searchTerm) =>
                          fetchOptionValues(searchTerm, true)
                        }
                        fetchMoreOptionsForSearch={fetchMoreOptionValues}
                        hasMoreOptions={hasMoreDestinationOptions}
                        selectedOptionLabels={selectedOptionLabels}
                        setSelectedOptionLabels={setSelectedOptionLabels}
                        openDropdownId={openDropdownId}
                        setOpenDropdownId={setOpenDropdownId}
                        loading={loadingStates.searchLoading}
                      />
                    </div>

                    <Button
                      variant="outline-danger"
                      className="ms-3 p-2 rounded-circle border-0"
                      onClick={() => {
                        const currentMappedValue = payloadForSave.mapped_option_value.find(
                          (m) => m.platformfielddata_id === val.id
                        )?.id;

                        setDeleteModel(true);
                        setSelectedMapId(currentMappedValue || null);
                      }}
                      title="Delete Mapping"
                    >
                      <FontAwesomeIcon icon={faTrashAlt} />
                    </Button>
                  </div>
                ))}
              </div>
            }  
          {/* )} */}
        </Modal.Body>

        {!shouldShowMappingLoader && optionData?.[0]?.attribute_field?.display_name !== undefined && <div className="d-flex align-items-center justify-content-center gap-3 my-3">
          <Button
            variant="outline-danger"
            className="rounded-circle p-2 d-flex align-items-center justify-content-center shadow-sm"
            style={{ width: "40px", height: "40px", transition: "all 0.2s" }}
            onClick={() =>
              setSkipFieldData((prev) => (prev === 0 ? prev : prev - 1))
            }
          >
            <FontAwesomeIcon icon={faMinus} />
          </Button>

          <span
            className="d-flex align-items-center justify-content-center fw-bold shadow-sm"
            style={{
              minWidth: "50px",
              height: "40px",
              borderRadius: "10px",
              backgroundColor: "#f8f9fa",
              border: "1px solid #dee2e6",
            }}
          >
            {skipFieldData+1}
          </span>

          <Button
            variant="outline-success"
            className="rounded-circle p-2 d-flex align-items-center justify-content-center shadow-sm"
            style={{ width: "40px", height: "40px", transition: "all 0.2s" }}
            onClick={() =>
              setSkipFieldData((prev) =>
                prev + 1 < optionValuePages ? prev + 1 : prev
              )
            }
          >
            <FontAwesomeIcon icon={faPlus} />
          </Button>
        </div>}




        <Modal.Footer className="justify-content-center">
          <Button variant="outline-secondary" onClick={resetForm}>
            Clear Mappings
          </Button>
          <Button
            variant="primary"
            disabled={isSaving || isFormLoading}
            onClick={() =>
              onSave({
                name: formState.mappingName,
                platform_id: item.id,
                attribute_template_id: formState.selectedValue,
                mapped_field_id: mappedFieldValue,
                attribute_field_id: attributeFieldId,
                mapped_option_value: payloadForSave.mapped_option_value,
              })
            }
          >
            {isSaving ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                Saving...
              </>
            ) : (
              "Save Mappings"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <CommonAlertModal
        key={'title description'}
        title={`Are You Ready to Continue?`}
        subtitle='Click "Yes" to proceed.'
        isVisible={deleteModel}
        onSuccess={handleDeleteMapping}
        onCancel={() => {
          setDeleteModel(false);
          setSelectedMapId(null);
        }}
        isTermsVisible={false}
      />
    </>
  );
};

export default AttributeAndOptionValueMappingModal;
